import React, { useState, useEffect, useRef } from "react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api";

// BUSINESS_TYPES removed — business type is now auto-detected from the website

// ─── Language options ────────────────────────────────────────────────────────
const LANGUAGES = [
  {
    code: "en-US",
    label: "English (US)",
    flag: "🇺🇸",
    greeting: (name) => `Hi, thanks for calling ${name}, this is Sara. How can I help you today?`,
    agentName: "Sara",
    description: "Standard American English",
  },
  {
    code: "en-GB",
    label: "English (UK)",
    flag: "🇬🇧",
    greeting: (name) => `Hello, thanks for calling ${name}, this is Sara. How may I help you today?`,
    agentName: "Sara",
    description: "British English accent",
  },
  {
    code: "ur",
    label: "اردو (Urdu)",
    flag: "🇵🇰",
    greeting: (name) => `السلام علیکم! ${name} میں خوش آمدید، میں سارہ ہوں۔ آپ کی کیا خدمت کر سکتی ہوں؟`,
    agentName: "سارہ",
    description: "Natural Urdu — Pakistani dialect",
    urdu: true,
  },
  {
    code: "ur-roman",
    label: "Urdu (Roman / Hinglish)",
    flag: "🇵🇰",
    greeting: (name) => `Assalam o Alaikum! ${name} mein aapka swaagat hai, main Sara bol rahi hoon. Aap ki kya khidmat kar sakti hoon?`,
    agentName: "Sara",
    description: "Roman Urdu — easy for Pakistani customers",
    urdu: true,
  },
  {
    code: "ar",
    label: "العربية (Arabic)",
    flag: "🇸🇦",
    greeting: (name) => `مرحباً، شكراً لاتصالك بـ ${name}، أنا سارة. كيف يمكنني مساعدتك اليوم؟`,
    agentName: "سارة",
    description: "Modern Standard Arabic",
  },
  {
    code: "hi",
    label: "हिन्दी (Hindi)",
    flag: "🇮🇳",
    greeting: (name) => `नमस्ते! ${name} में आपका स्वागत है, मैं सारा हूँ। आपकी क्या सेवा कर सकती हूँ?`,
    agentName: "Sara",
    description: "Hindi — Indian subcontinent",
  },
];

// SERVICES array removed — no longer needed (services are auto-detected from website)

const AMBIENT_SOUNDS = [
  { id:"",               label:"None" },
  { id:"coffee-shop",    label:"Coffee Shop" },
  { id:"call-center",    label:"Call Centre" },
  { id:"convention-hall",label:"Office Buzz" },
  { id:"static-noise",   label:"Static" },
];

const TRIAL_MINUTES = 10;
const FREE_DOMAINS = ["gmail.com","yahoo.com","hotmail.com","outlook.com","icloud.com","aol.com","protonmail.com","mail.com"];

function Steps({ current }) {
  const steps = ["Your Details", "Language & Voice", "Go Live"];
  return (
    <div className="flex items-center justify-center gap-1 mb-8 flex-wrap">
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div className={`flex items-center gap-1.5 ${i <= current ? "text-white" : "text-gray-600"}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
              i < current ? "bg-green-500 border-green-500" :
              i === current ? "bg-purple-600 border-purple-500" :
              "bg-transparent border-gray-700"}`}>
              {i < current ? "✓" : i + 1}
            </div>
            <span className="text-xs font-medium hidden sm:block">{s}</span>
          </div>
          {i < steps.length - 1 && <div className={`h-px w-6 ${i < current ? "bg-green-500" : "bg-gray-700"}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Language Selector ───────────────────────────────────────────────────────
function LanguageSelector({ selected, onSelect }) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-400 mb-3">
        Choose the language your AI receptionist will speak. Pakistani customers can interact naturally in Urdu.
      </p>
      <div className="grid grid-cols-1 gap-2">
        {LANGUAGES.map(lang => (
          <button
            key={lang.code}
            type="button"
            onClick={() => onSelect(lang.code)}
            className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
              selected === lang.code
                ? "border-purple-500 bg-purple-500/20 text-white"
                : "border-white/10 bg-white/5 text-gray-300 hover:border-white/30 hover:text-white"
            }`}
          >
            <span className="text-2xl flex-shrink-0">{lang.flag}</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">{lang.label}</p>
              <p className="text-xs text-gray-500">{lang.description}</p>
            </div>
            {lang.urdu && (
              <span className="flex-shrink-0 text-xs px-2 py-0.5 rounded-full bg-green-500/20 border border-green-500/40 text-green-400 font-semibold">
                🇵🇰 PK
              </span>
            )}
            {selected === lang.code && (
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs font-bold">✓</span>
            )}
          </button>
        ))}
      </div>
      {/* Preview greeting */}
      {selected && (() => {
        const lang = LANGUAGES.find(l => l.code === selected);
        return lang ? (
          <div className="mt-3 p-3 rounded-xl bg-black/30 border border-white/10">
            <p className="text-xs text-gray-500 mb-1">Preview greeting:</p>
            <p className={`text-sm text-purple-300 italic ${lang.code === "ur" ? "text-right font-urdu" : ""}`}>
              "{lang.greeting("Your Business")}"
            </p>
          </div>
        ) : null;
      })()}
    </div>
  );
}

function TrialBadge({ used = 0 }) {
  const pct = Math.min((used / TRIAL_MINUTES) * 100, 100);
  return (
    <div className="bg-gradient-to-r from-purple-900 to-blue-900 border border-purple-500 rounded-xl p-4">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-semibold text-white">Free Trial</span>
        <span className="text-xs text-purple-300">{Math.max(TRIAL_MINUTES - used, 0)} / {TRIAL_MINUTES} min left</span>
      </div>
      <div className="w-full bg-gray-700 rounded-full h-1.5">
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-gray-500 mt-2">Upgrade anytime to keep your AI receptionist running 24/7</p>
    </div>
  );
}

// ─── Uplift AI — Native Pakistani Urdu voices ─────────────────────────────────────────
const UPLIFT_VOICES = [
  { voice_id: "uplift-helpdesk-agent",    name: "Sara",    gender: "female", accent: "Urdu / Multilingual", age: "Young",       recommended: true,  note: "Warm & professional female — speaks natural Urdu" },
  { voice_id: "uplift-broadband-support", name: "Ahmed",   gender: "male",   accent: "Urdu / Multilingual", age: "Young",       recommended: false, note: "Friendly male — helpful & patient support style" },
  { voice_id: "uplift-prime-time-anchor", name: "Hassan",  gender: "male",   accent: "Urdu / Multilingual", age: "Middle Aged", recommended: false, note: "Deep & authoritative — news anchor style" },
  { voice_id: "uplift-field-correspondent", name: "Imran", gender: "male",   accent: "Urdu / Multilingual", age: "Young",       recommended: false, note: "Energetic reporter style — great for updates" },
  { voice_id: "uplift-street-vlogger",    name: "Zain",    gender: "male",   accent: "Urdu / Multilingual", age: "Young",       recommended: false, note: "Casual & relatable — perfect for younger audience" },
];

function UrduVoiceSelector({ selected, onSelect, language }) {
  const [playing, setPlaying] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(null);
  const audioRef = useRef(null);

  const playPreview = async (voice) => {
    if (playing === voice.voice_id) {
      audioRef.current?.pause();
      setPlaying(null);
      return;
    }
    setLoadingPreview(voice.voice_id);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/voices/preview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          voice_id: voice.voice_id,
          language: language || "ur",
        }),
      });
      if (!res.ok) throw new Error("Preview failed");
      const blob = await res.blob();
      if (audioRef.current) audioRef.current.pause();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setPlaying(null); URL.revokeObjectURL(url); };
      await audio.play();
      setPlaying(voice.voice_id);
    } catch {
      setPlaying(null);
    } finally {
      setLoadingPreview(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/30 text-blue-200 text-xs space-y-1">
        <p className="font-bold text-blue-300">🇵🇰 Uplift AI — Pakistani Urdu only</p>
        <p>Native Urdu pronunciation. Speech is recognized in Urdu (not Hindi). Click ▶ to preview.</p>
      </div>
      <div className="space-y-2">
        {UPLIFT_VOICES.map(v => (
          <div key={v.voice_id} onClick={() => onSelect(v.voice_id)}
            className={`relative flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
              selected === v.voice_id
                ? "border-purple-500 bg-purple-500/20"
                : "border-white/10 bg-white/5 hover:border-purple-400/50"}`}>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg flex-shrink-0 ${
              v.gender === "female" ? "bg-pink-500/20" : "bg-blue-500/20"}`}>
              {v.gender === "female" ? "👩" : "👨"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-white text-sm font-bold">{v.name}</p>
                {v.recommended && (
                  <span className="px-1.5 py-0.5 rounded text-xs bg-green-500/20 text-green-400 border border-green-500/30">
                    Recommended
                  </span>
                )}
              </div>
              <p className="text-gray-400 text-xs">{v.accent} · {v.age}</p>
              <p className="text-gray-500 text-xs mt-0.5">{v.note}</p>
            </div>
            <button type="button" onClick={e => { e.stopPropagation(); playPreview(v); }}
              className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs transition-all ${
                playing === v.voice_id
                  ? "bg-purple-600 text-white"
                  : "bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white"}`}
              title="Preview Urdu voice">
              {loadingPreview === v.voice_id ? "…" : playing === v.voice_id ? "■" : "▶"}
            </button>
            {selected === v.voice_id && (
              <div className="w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">✓</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function VoiceSelector({ voices, selected, onSelect, language }) {
  const [filter, setFilter] = useState("all");
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);

  const isSouthAsian = ["hi"].includes(language);

  const SA_KEYWORDS = ["indian", "hindi", "south asian", "priya", "meera",
                       "ananya", "raj", "arjun", "rohan", "vikram"];

  const isSouthAsianVoice = (v) => {
    const label = `${v.voice_id} ${v.name} ${v.accent || ""} ${v.description || ""}`.toLowerCase();
    return SA_KEYWORDS.some(kw => label.includes(kw));
  };

  const filtered = voices.filter(v => filter === "all" ? true : v.gender?.toLowerCase() === filter);

  const sorted = isSouthAsian
    ? [...filtered].sort((a, b) => (isSouthAsianVoice(b) ? 1 : 0) - (isSouthAsianVoice(a) ? 1 : 0))
    : filtered;

  const playPreview = async (voice) => {
    if (playing === voice.voice_id) {
      audioRef.current?.pause();
      setPlaying(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    if (voice.preview_url) {
      const audio = new Audio(voice.preview_url);
      audioRef.current = audio;
      audio.play().catch(() => {});
      setPlaying(voice.voice_id);
      audio.onended = () => setPlaying(null);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/voices/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ voice_id: voice.voice_id, language }),
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setPlaying(null); URL.revokeObjectURL(url); };
      await audio.play();
      setPlaying(voice.voice_id);
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-3">
      {language?.startsWith("en") && (
        <div className="p-2.5 rounded-xl bg-purple-500/10 border border-purple-500/30 text-purple-200 text-xs">
          Low-latency English voices (Cartesia / OpenAI). Speech recognized in English.
        </div>
      )}
      <div className="flex gap-2">
        {["all","female","male"].map(f => (
          <button key={f} type="button" onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
              filter === f ? "bg-purple-600 text-white" : "bg-white bg-opacity-10 text-gray-400 hover:text-white"}`}>
            {f === "all" ? "All" : f === "female" ? "Female" : "Male"}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
        {sorted.map(v => {
          const isSA = isSouthAsianVoice(v);
          return (
            <div key={v.voice_id} onClick={() => onSelect(v.voice_id)}
              className={`relative flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                selected === v.voice_id
                  ? "border-purple-500 bg-purple-500 bg-opacity-20"
                  : isSA && isSouthAsian
                  ? "border-green-500/40 bg-green-500/5 hover:border-green-400/60"
                  : "border-white border-opacity-10 hover:border-white hover:border-opacity-30 bg-white bg-opacity-5"}`}>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                v.gender?.toLowerCase() === "female" ? "bg-pink-500 bg-opacity-20 text-pink-400" : "bg-blue-500 bg-opacity-20 text-blue-400"}`}>
                {isSA && isSouthAsian ? "🇵🇰" : (v.gender?.toLowerCase() === "female" ? "F" : "M")}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-semibold truncate">{v.name}</p>
                <p className="text-gray-500 text-xs">{v.age}{v.accent ? ` · ${v.accent}` : ""}</p>
              </div>
              <button type="button" onClick={e => { e.stopPropagation(); playPreview(v); }}
                className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs transition-all ${
                  playing === v.voice_id
                    ? "bg-purple-600 text-white"
                    : "bg-white bg-opacity-10 text-gray-400 hover:bg-white hover:bg-opacity-20 hover:text-white"}`}>
                {playing === v.voice_id ? "■" : "▶"}
              </button>
              {selected === v.voice_id && (
                <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {selected && voices.find(v => v.voice_id === selected) && (
        <p className="text-xs text-purple-300 text-center">
          Selected: <strong>{voices.find(v => v.voice_id === selected).name}</strong> — click ▶ to preview
        </p>
      )}
    </div>
  );
}

function AmbientPicker({ selected, onSelect }) {
  const labels = { "": "None", "coffee-shop": "Coffee Shop", "call-center": "Call Centre", "convention-hall": "Office Buzz", "static-noise": "Static" };
  return (
    <div className="flex gap-2 flex-wrap">
      {AMBIENT_SOUNDS.map(s => (
        <button key={s.id} type="button" onClick={() => onSelect(s.id)}
          className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${
            selected === s.id
              ? "border-purple-500 bg-purple-500 bg-opacity-20 text-white"
              : "border-white border-opacity-10 text-gray-400 hover:border-white hover:border-opacity-30 hover:text-white"}`}>
          {labels[s.id] || s.label}
        </button>
      ))}
    </div>
  );
}

function LiveDashboard({ receptionist, voices, onDelete, onEdit }) {
  const [callState, setCallState] = useState("idle");
  const [transcript, setTranscript] = useState([]);
  const [agentTalking, setAgentTalking] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editVoice, setEditVoice] = useState(receptionist.voice_id || "cartesia-Grace");
  const [editAmbient, setEditAmbient] = useState(receptionist.ambient_sound || "call-center");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const clientRef = useRef(null);

  // Detect if this is an Urdu receptionist
  const isUrduReceptionist = receptionist.voice_id?.startsWith("uplift-") || receptionist.source === "vapi";
  const editVoices = isUrduReceptionist ? UPLIFT_VOICES : voices;

  const startWebCall = async () => {
    setCallState("connecting");
    setTranscript([]);
    try {
      // Request microphone permission upfront for clear error messaging
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (micErr) {
        throw new Error("Microphone access denied — please enable it in your browser settings");
      }

      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/receptionist/retell/web-call`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ receptionist_id: receptionist.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to start call");

      if (data.provider === "vapi") {
        // Vapi web SDK — public key initialises the SDK, agent_id starts the call
        const Vapi = (await import("@vapi-ai/web")).default;
        const vapi = new Vapi(data.access_token);
        clientRef.current = { stopCall: () => vapi.stop(), _type: "vapi" };

        vapi.on("call-start",        () => setCallState("active"));
        vapi.on("call-end",          () => { setCallState("ended"); setAgentTalking(false); });
        vapi.on("speech-start",      () => setAgentTalking(true));
        vapi.on("speech-end",        () => setAgentTalking(false));
        vapi.on("message", m => {
          if (m.type === "transcript") {
            setTranscript(prev => [...prev, { role: m.role, content: m.transcript }].slice(-8));
          }
        });
        vapi.on("error", e => {
          console.error("Vapi error:", e);
          setCallState("idle");
          const msg = e?.errorMsg || e?.message || e?.error?.message || (typeof e === "string" ? e : JSON.stringify(e));
          alert("Call ended: " + (msg || "Connection issue — try again"));
        });

        // Use fresh agent_id from backend (not stale one from receptionist record)
        const agentId = data.agent_id || receptionist.agent_id;
        const call = await vapi.start(agentId);
        if (!call) throw new Error("Failed to start call — check microphone permissions");
      } else {
        // Retell web SDK
        const { RetellWebClient } = await import("retell-client-js-sdk");
        const client = new RetellWebClient();
        clientRef.current = client;

        client.on("call_started",        () => setCallState("active"));
        client.on("call_ended",          () => { setCallState("ended"); setAgentTalking(false); });
        client.on("agent_start_talking", () => setAgentTalking(true));
        client.on("agent_stop_talking",  () => setAgentTalking(false));
        client.on("update", u => { if (u.transcript) setTranscript(u.transcript.slice(-8)); });
        client.on("error",  e => {
          console.error("Retell error:", e);
          setCallState("idle");
          const errMsg = typeof e === 'string' ? e : (e?.message || e?.error || e?.errorMessage || JSON.stringify(e) || "Connection issue — try again");
          alert("Call ended: " + errMsg);
        });

        await client.startCall({ accessToken: data.access_token });
      }
    } catch (err) {
      alert("Error: " + err.message);
      setCallState("idle");
    }
  };

  const stopCall = () => {
    if (clientRef.current) { clientRef.current.stopCall(); clientRef.current = null; }
    setCallState("ended");
    setAgentTalking(false);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/receptionist/retell/${receptionist.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ voice_id: editVoice, ambient_sound: editAmbient }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Update failed");
      setShowEdit(false);
      onEdit({ ...receptionist, voice_id: editVoice, ambient_sound: editAmbient });
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete the AI receptionist for "' + receptionist.business_name + '"? This cannot be undone.')) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/receptionist/retell/${receptionist.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Delete failed");
      onDelete();
    } catch (err) {
      alert("Error: " + err.message);
      setDeleting(false);
    }
  };

  const currentVoiceName = (() => {
    const vid = receptionist.voice_id || editVoice;
    const found = voices.find(v => v.voice_id === vid);
    if (found) return found.name;
    const uplift = UPLIFT_VOICES.find(v => v.voice_id === vid);
    if (uplift) return uplift.name;
    if (vid && vid.includes("-")) {
      const parts = vid.split("-");
      return parts[parts.length - 1].charAt(0).toUpperCase() + parts[parts.length - 1].slice(1);
    }
    return "Grace";
  })();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between bg-green-500 bg-opacity-10 border border-green-500 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <span className="w-3 h-3 rounded-full bg-green-400 animate-pulse flex-shrink-0" />
          <div>
            <p className="text-white font-semibold">{receptionist.business_name} — Active</p>
            <p className="text-xs text-gray-400">Voice: {currentVoiceName} · Agent: {receptionist.agent_id ? receptionist.agent_id.slice(0,20) + "..." : "N/A"}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowEdit(!showEdit)}
            className="px-3 py-1.5 bg-white bg-opacity-10 hover:bg-opacity-20 text-white text-xs rounded-lg transition-all">
            Edit
          </button>
          <button onClick={handleDelete} disabled={deleting}
            className="px-3 py-1.5 bg-red-500 bg-opacity-20 hover:bg-opacity-40 text-red-300 text-xs rounded-lg transition-all disabled:opacity-50">
            {deleting ? "..." : "Delete"}
          </button>
        </div>
      </div>

      {showEdit && (
        <div className="bg-white bg-opacity-5 border border-purple-500 border-opacity-50 rounded-xl p-5 space-y-4">
          <h3 className="text-white font-semibold">Edit Receptionist</h3>
          <div>
            <label className="text-xs text-gray-400 mb-2 block">Voice {isUrduReceptionist && <span className="text-purple-400">(Urdu)</span>}</label>
            {isUrduReceptionist ? (
              <UrduVoiceSelector selected={editVoice} onSelect={setEditVoice} language="ur" />
            ) : (
              <VoiceSelector voices={editVoices} selected={editVoice} onSelect={setEditVoice} />
            )}
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-2 block">Ambient Sound</label>
            <AmbientPicker selected={editAmbient} onSelect={setEditAmbient} />
          </div>
          <div className="flex gap-2">
            <button onClick={handleSaveEdit} disabled={saving}
              className="flex-1 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50 transition-all">
              {saving ? "Saving..." : "Save Changes"}
            </button>
            <button onClick={() => setShowEdit(false)}
              className="px-4 py-2 bg-white bg-opacity-10 hover:bg-opacity-20 text-gray-300 rounded-lg text-sm transition-all">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-white bg-opacity-5 border border-white border-opacity-10 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-semibold">Test in Browser — Free</h3>
            <p className="text-xs text-gray-400">Talk to your AI receptionist right now, no phone needed</p>
          </div>
          {callState === "active" && (
            <div className={`flex items-center gap-2 text-xs px-3 py-1 rounded-full border transition-all ${
              agentTalking ? "border-purple-400 text-purple-300 bg-purple-500 bg-opacity-20" : "border-gray-600 text-gray-400"}`}>
              <span className={`w-2 h-2 rounded-full ${agentTalking ? "bg-purple-400 animate-pulse" : "bg-gray-600"}`} />
              {agentTalking ? "AI speaking..." : "Listening..."}
            </div>
          )}
        </div>

        {(callState === "idle" || callState === "ended") && (
          <button onClick={startWebCall}
            className="w-full py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-xl font-semibold hover:opacity-90 transition-all">
            {callState === "ended" ? "Call Again" : "Start Browser Call"}
          </button>
        )}
        {callState === "connecting" && (
          <button disabled className="w-full py-3 bg-gray-700 text-gray-400 rounded-xl font-semibold flex items-center justify-center gap-2">
            <span className="animate-spin">...</span> Connecting...
          </button>
        )}
        {callState === "active" && (
          <button onClick={stopCall}
            className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-all">
            End Call
          </button>
        )}

        {(callState === "active" || (callState === "ended" && transcript.length > 0)) && (
          <div className="bg-black bg-opacity-30 rounded-xl p-3 space-y-1.5 max-h-44 overflow-y-auto">
            <p className="text-xs text-gray-600 mb-1">Live transcript</p>
            {transcript.length === 0
              ? <p className="text-xs text-gray-500 italic">Waiting for speech...</p>
              : transcript.map((line, i) => (
                  <p key={i} className={`text-xs leading-relaxed ${line.role === "agent" ? "text-purple-300" : "text-white"}`}>
                    <span className="font-semibold opacity-60">{line.role === "agent" ? "AI" : "You"}: </span>
                    {line.content}
                  </p>
                ))
            }
          </div>
        )}
        {callState === "ended" && (
          <p className="text-xs text-center text-gray-600">Call ended — full transcript in your Retell dashboard</p>
        )}
      </div>

      <div className="bg-blue-500 bg-opacity-10 border border-blue-500 rounded-xl p-4">
        <h3 className="text-white font-semibold mb-1">Go Live</h3>
        <p className="text-xs text-gray-300 mb-3">Buy a Retell number (~$2/mo) and forward your business number to it.</p>
        <a href="https://retellai.com/dashboard" target="_blank" rel="noreferrer"
          className="inline-block text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-all">
          Get a phone number
        </a>
      </div>

      <TrialBadge used={receptionist.minutes_used || 0} />
    </div>
  );
}

export default function AIReceptionistTab() {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [error, setError] = useState("");
  const [activeReceptionist, setActiveReceptionist] = useState(null);
  const [voices, setVoices] = useState([]);

  const [firstName, setFirstName]         = useState("");
  const [businessName, setBusinessName]   = useState("");
  const [businessEmail, setBusinessEmail] = useState("");
  const [businessPhone, setBusinessPhone] = useState("");
  const [businessUrl, setBusinessUrl]     = useState("");
  const [emailError, setEmailError]       = useState("");
  const [scraping, setScraping]           = useState(false);
  const [scraped, setScraped]             = useState(null);
  const [showScrapedDetails, setShowScrapedDetails] = useState(false);

  const [voiceId, setVoiceId]           = useState("cartesia-Grace");
  const [ambientSound, setAmbientSound] = useState("call-center");
  const [language, setLanguage]         = useState("en-US");
  const [agentName, setAgentName]       = useState("Sara");

  // Auto-set best voice when language changes
  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    if (lang === "ur" || lang === "ur-roman") setVoiceId("uplift-helpdesk-agent");
    else if (lang === "hi") setVoiceId("11labs-Monika");
    else if (lang === "ar") setVoiceId("11labs-Rachel");
    else setVoiceId("cartesia-Grace");
  };
  
  // Additional state variables for business information
  const [hours, setHours]               = useState("Monday to Friday, 9am to 5pm. Saturday 10am to 2pm. Closed Sunday.");
  const [address, setAddress]           = useState("");
  
  // Call pickup rules
  const [callPickupRules, setCallPickupRules] = useState({
    answerAllCalls: true,
    onlyDuringHours: false,
    onlyAfterHours: false,
    customSchedule: false,
  });
  
  // Calendar integration
  const [calendarEnabled, setCalendarEnabled] = useState(false);
  const [calendarLink, setCalendarLink] = useState("");
  const [calendarProvider, setCalendarProvider] = useState("cal.com"); // cal.com, calendly, google

  const loadVoices = (lang) => {
    const token = localStorage.getItem("token");
    if (!token) return;
    const q = lang ? `?language=${encodeURIComponent(lang)}` : "";
    fetch(`${API_URL}/voices${q}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setVoices(Array.isArray(data) ? data : []))
      .catch(() => {});
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    loadVoices(language);
    fetch(`${API_URL}/receptionist/list`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const list = data.items || data.receptionists || [];
        const active = list.find(r => r.status === "active");
        if (active) { setActiveReceptionist(active); setStep(2); }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (language === "ur" || language === "ur-roman") return;
    loadVoices(language);
  }, [language]);

  const validateEmail = (email) => {
    const domain = email.split("@")[1] ? email.split("@")[1].toLowerCase() : "";
    if (FREE_DOMAINS.includes(domain)) {
      setEmailError("Please use your company email (not Gmail, Yahoo, etc.)");
      return false;
    }
    setEmailError("");
    return true;
  };

  const [, setScrapeProgress] = useState("");

  const handleScrape = async () => {
    if (!businessUrl.trim()) return;
    setScraping(true);
    setScraped(null);
    setError("");
    setScrapeProgress("Connecting to website...");
    try {
      const token = localStorage.getItem("token");

      // Progress simulation while real fetch happens
      const progressSteps = [
        { delay: 2000, msg: "Scanning pages for business info..." },
        { delay: 5000, msg: "Extracting services & contact details..." },
        { delay: 9000, msg: "Analyzing with AI for complete data..." },
        { delay: 14000, msg: "Checking contact & about pages..." },
        { delay: 20000, msg: "Finalizing extraction..." },
      ];
      const timers = progressSteps.map(s => setTimeout(() => setScrapeProgress(s.msg), s.delay));

      const res = await fetch(`${API_URL}/receptionist/scrape`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ url: businessUrl, deep_scrape: true }),
      });
      const data = await res.json();
      timers.forEach(t => clearTimeout(t));

      if (!res.ok) throw new Error(data.error || "Scrape failed");
      setScraped(data);
      if (data.name && !businessName) setBusinessName(data.name);
      if (data.hours) setHours(data.hours);
      if (data.address) setAddress(data.address);
      if (data.phone && !businessPhone) setBusinessPhone(data.phone);
      if (data.email && !businessEmail) setBusinessEmail(data.email);
      setScrapeProgress("");
    } catch (err) {
      setError("Could not fetch website data: " + err.message);
      setScrapeProgress("");
    } finally {
      setScraping(false);
    }
  };

  const resetAll = () => {
    setStep(0); setActiveReceptionist(null);
    setFirstName(""); setBusinessName(""); setBusinessEmail(""); setBusinessPhone("");
    setBusinessUrl(""); setScraped(null);
    setVoiceId("cartesia-Grace"); setAmbientSound("call-center"); setLanguage("en-US"); setAgentName("Sara"); setError("");
  };

  const handleStep1 = e => {
    e.preventDefault();
    if (!validateEmail(businessEmail)) return;
    setError(""); setStep(1);
  };

  const handleActivate = async e => {
    e.preventDefault();
    setError(""); setLoading(true); setLoadingStep(1);
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("Please log in first");

      // Simulate step progression for UX
      const stepTimer = setInterval(() => {
        setLoadingStep(prev => prev < 3 ? prev + 1 : prev);
      }, 800);

      const res = await fetch(`${API_URL}/receptionist/retell/setup`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          business_name: businessName,
          business_url: businessUrl,
          voice_id: voiceId,
          ambient_sound: ambientSound,
          language: language,
          agent_name: agentName,
          contact: { first_name: firstName, email: businessEmail, phone: businessPhone },
          services: scraped?.services || [],
          pricing: scraped?.pricing || [],
          hours: scraped?.hours || hours,
          address: scraped?.address || address,
          business_type: scraped?.business_type || "",
          city: scraped?.location || "",
          phone: businessPhone,
          call_pickup_rules: callPickupRules,
          calendar_enabled: calendarEnabled,
          calendar_link: calendarLink,
          calendar_provider: calendarProvider,
          // Deep knowledge for comprehensive AI receptionist
          description: scraped?.description || "",
          specialties: scraped?.specialties || [],
          unique_selling_points: scraped?.unique_selling_points || [],
          experience: scraped?.experience || "",
          team: scraped?.team || "",
          faqs: scraped?.faqs || [],
          policies: scraped?.policies || [],
          payment_methods: scraped?.payment_methods || [],
          promotions: scraped?.promotions || [],
          testimonials: scraped?.testimonials || [],
          appointments: scraped?.appointments || "",
          extra: scraped?.extra || {},
        }),
      });

      clearInterval(stepTimer);
      setLoadingStep(4);

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Setup failed");

      setActiveReceptionist({
        id: data.receptionist_id,
        business_name: businessName,
        agent_id: data.agent_id,
        phone_number: null,
        status: "active",
        source: "retell",
        voice_id: voiceId,
        ambient_sound: ambientSound,
        minutes_used: 0,
      });
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingStep(0);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-white mb-1">AI Receptionist</h2>
        <p className="text-gray-400 text-sm">Turn missed calls into booked jobs — automatically</p>
      </div>

      {step < 2 && <Steps current={step} />}

      {error && (
        <div className="bg-red-500 bg-opacity-20 border border-red-500 rounded-xl p-3 text-red-300 text-sm">{error}</div>
      )}

      {step === 0 && (
        <form onSubmit={handleStep1} className="bg-white bg-opacity-5 border border-white border-opacity-10 rounded-2xl p-6 space-y-4">
          <h3 className="text-white font-semibold text-lg">Tell us about your business</h3>

          <div>
            <label className="text-xs text-gray-400 mb-1 block">Business Website — we extract everything automatically</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={businessUrl} onChange={e => setBusinessUrl(e.target.value)}
                placeholder="https://yourbusiness.com"
                className="flex-1 min-w-0 px-3 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
              <button type="button" onClick={handleScrape} disabled={scraping || !businessUrl.trim()}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white rounded-lg text-sm font-semibold transition-all whitespace-nowrap flex-shrink-0">
                {scraping ? "Fetching..." : "Fetch Info"}
              </button>
            </div>
          </div>

          {scraped && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3">
              {/* Always-visible summary row */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <span className="text-green-400 text-xs font-semibold shrink-0">✅ Extracted</span>
                  {scraped.name && <span className="text-white text-xs font-semibold truncate">{scraped.name}</span>}
                  {scraped.business_type && <span className="text-purple-300 text-xs truncate">· {scraped.business_type}</span>}
                  {scraped.hours && <span className="text-gray-400 text-xs truncate">· {scraped.hours}</span>}
                  {scraped.phone && <span className="text-gray-400 text-xs truncate">· {scraped.phone}</span>}
                  <span className="text-gray-500 text-xs shrink-0">
                    {[scraped.services?.length && `${scraped.services.length} services`, scraped.pricing?.length && `${scraped.pricing.length} prices`, scraped.faqs?.length && `${scraped.faqs.length} FAQs`].filter(Boolean).join(" · ")}
                  </span>
                </div>
                <button type="button" onClick={() => setShowScrapedDetails(!showScrapedDetails)}
                  className="text-purple-400 hover:text-purple-300 text-xs font-medium shrink-0 underline">
                  {showScrapedDetails ? "Hide" : "View Details"}
                </button>
              </div>
              <p className="text-xs text-green-400/60 mt-1.5">AI receptionist will use this to answer calls like a knowledgeable employee</p>

              {/* Expandable details */}
              {showScrapedDetails && (
                <div className="mt-3 border-t border-green-500/20 pt-3 space-y-2 max-h-64 overflow-y-auto text-xs">
                  {scraped.description && <div><span className="text-gray-400">Description:</span> <span className="text-gray-200">{scraped.description}</span></div>}
                  {scraped.phone && <div><span className="text-gray-400">Phone:</span> <span className="text-white">{scraped.phone}</span></div>}
                  {scraped.email && <div><span className="text-gray-400">Email:</span> <span className="text-white">{scraped.email}</span></div>}
                  {scraped.address && <div><span className="text-gray-400">Address:</span> <span className="text-white">{scraped.address}</span></div>}
                  {scraped.hours && <div><span className="text-gray-400">Hours:</span> <span className="text-white">{scraped.hours}</span></div>}
                  {scraped.location && <div><span className="text-gray-400">Location:</span> <span className="text-white">{scraped.location}</span></div>}
                  {scraped.experience && <div><span className="text-gray-400">Experience:</span> <span className="text-white">{scraped.experience}</span></div>}
                  {scraped.appointments && <div><span className="text-gray-400">Appointments:</span> <span className="text-white">{scraped.appointments}</span></div>}
                  {scraped.services?.length > 0 && (
                    <div><span className="text-gray-400">Services:</span> <span className="text-gray-200">{scraped.services.join(", ")}</span></div>
                  )}
                  {scraped.specialties?.length > 0 && (
                    <div><span className="text-gray-400">Specialties:</span> <span className="text-gray-200">{scraped.specialties.join(", ")}</span></div>
                  )}
                  {scraped.pricing?.length > 0 && (
                    <div><span className="text-gray-400">Pricing:</span> <span className="text-gray-200">{scraped.pricing.join(" | ")}</span></div>
                  )}
                  {scraped.payment_methods?.length > 0 && (
                    <div><span className="text-gray-400">Payment:</span> <span className="text-gray-200">{scraped.payment_methods.join(", ")}</span></div>
                  )}
                  {scraped.policies?.length > 0 && (
                    <div><span className="text-gray-400">Policies:</span> <span className="text-gray-200">{scraped.policies.join(" | ")}</span></div>
                  )}
                  {scraped.faqs?.length > 0 && (
                    <div><span className="text-gray-400">FAQs ({scraped.faqs.length}):</span> <span className="text-gray-200">{scraped.faqs.slice(0, 5).join(" | ")}</span></div>
                  )}
                  {scraped.team && <div><span className="text-gray-400">Team:</span> <span className="text-gray-200">{scraped.team}</span></div>}
                  {scraped.unique_selling_points?.length > 0 && (
                    <div><span className="text-gray-400">USPs:</span> <span className="text-gray-200">{scraped.unique_selling_points.join(", ")}</span></div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">First Name *</label>
              <input required value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Sarah"
                className="w-full px-3 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Business Name *</label>
              <input required value={businessName} onChange={e => setBusinessName(e.target.value)} placeholder="Acme Plumbing Ltd"
                className="w-full px-3 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
            </div>
          </div>

          {/* AI Receptionist Name */}
          <div>
            <label className="text-xs text-gray-400 mb-1 block">
              AI Receptionist Name *
              <span className="ml-2 text-gray-600">— what callers will hear</span>
            </label>
            <input
              required
              value={agentName}
              onChange={e => setAgentName(e.target.value)}
              placeholder="Sara, Ayesha, Zara, Alex..."
              className="w-full px-3 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <p className="text-xs text-gray-600 mt-1">Choose a name that fits your brand. Pakistani names like Ayesha, Zara, Hira work great for local businesses.</p>
          </div>

          <div>
            <label className="text-xs text-gray-400 mb-1 block">Business Email * (company email only)</label>
            <input required type="email" value={businessEmail}
              onChange={e => { setBusinessEmail(e.target.value); if (emailError) validateEmail(e.target.value); }}
              onBlur={e => validateEmail(e.target.value)} placeholder="sarah@acmeplumbing.com"
              className={`w-full px-3 py-2 bg-white bg-opacity-10 border rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 ${emailError ? "border-red-500" : "border-white border-opacity-20"}`} />
            {emailError && <p className="text-red-400 text-xs mt-1">{emailError}</p>}
          </div>

          <div>
            <label className="text-xs text-gray-400 mb-1 block">Business Phone *</label>
            <input required type="tel" value={businessPhone} onChange={e => setBusinessPhone(e.target.value)} placeholder="+1 (555) 000-0000"
              className="w-full px-3 py-2 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" />
          </div>

          <button type="submit" className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:opacity-90 transition-all">
            Continue
          </button>
          <p className="text-center text-xs text-gray-500">{TRIAL_MINUTES} free minutes — no credit card needed</p>
        </form>
      )}

      {step === 1 && (
        <form onSubmit={handleActivate} className="bg-white bg-opacity-5 border border-white border-opacity-10 rounded-2xl p-6 space-y-5">
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setStep(0)} className="text-gray-400 hover:text-white text-sm">Back</button>
            <h3 className="text-white font-semibold text-lg">Language & Voice</h3>
          </div>

          {/* ── Language selector ── */}
          <div>
            <label className="text-xs text-gray-400 mb-2 block font-semibold uppercase tracking-wide">
              🌐 Receptionist Language
            </label>
            <LanguageSelector selected={language} onSelect={handleLanguageChange} />
          </div>

          {/* ── Voice selector ── */}
          <div>
            <label className="text-xs text-gray-400 mb-2 block font-semibold uppercase tracking-wide">
              🎙️ Voice
            </label>
            {(language === "ur" || language === "ur-roman")
              ? <UrduVoiceSelector selected={voiceId} onSelect={setVoiceId} language={language} />
              : voices.length > 0
                ? <VoiceSelector voices={voices} selected={voiceId} onSelect={setVoiceId} language={language} />
                : <p className="text-gray-500 text-sm text-center py-4">Loading English voices...</p>
            }
          </div>

          {/* ── Ambient sound ── */}
          <div>
            <label className="text-xs text-gray-400 mb-2 block font-semibold uppercase tracking-wide">
              🔊 Ambient Background Sound
            </label>
            <p className="text-xs text-gray-600 mb-2">Subtle background audio makes the AI sound more natural on calls</p>
            <AmbientPicker selected={ambientSound} onSelect={setAmbientSound} />
          </div>

          {/* ── Call Pickup Rules ── */}
          <div className="border-t border-white/10 pt-5">
            <label className="text-xs text-gray-400 mb-3 block font-semibold uppercase tracking-wide">
              📞 When Should AI Answer Calls?
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 rounded-xl border border-white/10 bg-white/5 cursor-pointer hover:border-purple-500/50 transition-all">
                <input
                  type="radio"
                  name="callPickup"
                  checked={callPickupRules.answerAllCalls}
                  onChange={() => setCallPickupRules({ answerAllCalls: true, onlyDuringHours: false, onlyAfterHours: false, customSchedule: false })}
                  className="w-4 h-4 text-purple-600"
                />
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">Answer All Calls (24/7)</p>
                  <p className="text-gray-500 text-xs">AI receptionist answers every call, anytime</p>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 rounded-xl border border-white/10 bg-white/5 cursor-pointer hover:border-purple-500/50 transition-all">
                <input
                  type="radio"
                  name="callPickup"
                  checked={callPickupRules.onlyDuringHours}
                  onChange={() => setCallPickupRules({ answerAllCalls: false, onlyDuringHours: true, onlyAfterHours: false, customSchedule: false })}
                  className="w-4 h-4 text-purple-600"
                />
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">Only During Business Hours</p>
                  <p className="text-gray-500 text-xs">AI answers only when you're open ({hours.split('.')[0]})</p>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 rounded-xl border border-white/10 bg-white/5 cursor-pointer hover:border-purple-500/50 transition-all">
                <input
                  type="radio"
                  name="callPickup"
                  checked={callPickupRules.onlyAfterHours}
                  onChange={() => setCallPickupRules({ answerAllCalls: false, onlyDuringHours: false, onlyAfterHours: true, customSchedule: false })}
                  className="w-4 h-4 text-purple-600"
                />
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">Only After Hours</p>
                  <p className="text-gray-500 text-xs">AI handles calls when you're closed — perfect for overflow</p>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 rounded-xl border border-white/10 bg-white/5 cursor-pointer hover:border-purple-500/50 transition-all">
                <input
                  type="radio"
                  name="callPickup"
                  checked={callPickupRules.customSchedule}
                  onChange={() => setCallPickupRules({ answerAllCalls: false, onlyDuringHours: false, onlyAfterHours: false, customSchedule: true })}
                  className="w-4 h-4 text-purple-600"
                />
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">Custom Schedule</p>
                  <p className="text-gray-500 text-xs">Set specific days/times (configure in dashboard after setup)</p>
                </div>
              </label>
            </div>
          </div>

          {/* ── Calendar Integration ── */}
          <div className="border-t border-white/10 pt-5">
            <label className="text-xs text-gray-400 mb-3 block font-semibold uppercase tracking-wide">
              📅 Appointment Booking
            </label>
            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 rounded-xl border border-white/10 bg-white/5 cursor-pointer hover:border-purple-500/50 transition-all">
                <input
                  type="checkbox"
                  checked={calendarEnabled}
                  onChange={(e) => setCalendarEnabled(e.target.checked)}
                  className="w-4 h-4 text-purple-600 rounded"
                />
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">Enable Appointment Booking</p>
                  <p className="text-gray-500 text-xs">AI can book appointments directly into your calendar</p>
                </div>
              </label>

              {calendarEnabled && (
                <div className="space-y-3 pl-7">
                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">Calendar Provider</label>
                    <select
                      value={calendarProvider}
                      onChange={(e) => setCalendarProvider(e.target.value)}
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="cal.com">Cal.com</option>
                      <option value="calendly">Calendly</option>
                      <option value="google">Google Calendar</option>
                      <option value="outlook">Outlook Calendar</option>
                      <option value="custom">Custom Booking Link</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-gray-400 mb-1 block">
                      Booking Link
                      <span className="ml-2 text-gray-600">— AI will share this with callers</span>
                    </label>
                    <input
                      type="url"
                      value={calendarLink}
                      onChange={(e) => setCalendarLink(e.target.value)}
                      placeholder="https://cal.com/yourbusiness/appointment"
                      className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      {calendarProvider === "cal.com" && "Get your free Cal.com link at cal.com"}
                      {calendarProvider === "calendly" && "Find your Calendly link in your account settings"}
                      {calendarProvider === "google" && "Create a booking page with Google Calendar appointment slots"}
                      {calendarProvider === "outlook" && "Use Microsoft Bookings to create your booking link"}
                      {calendarProvider === "custom" && "Paste any booking link that works for your business"}
                    </p>
                  </div>

                  <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/30 text-blue-300 text-xs">
                    <p className="font-semibold mb-1">💡 How it works:</p>
                    <p>When a caller wants to book, the AI will collect their details and either:</p>
                    <ul className="list-disc list-inside mt-1 space-y-0.5 text-blue-300/80">
                      <li>Share your booking link for them to choose a time</li>
                      <li>Take their preferred time and send you a notification to confirm</li>
                      <li>Automatically book if you connect your calendar API (advanced)</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div className="space-y-2">
              {["Building your AI receptionist", "Configuring voice and personality", "Connecting to Retell AI", "Going live"].map((label, i) => (
                <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                  loadingStep > i + 1 ? "bg-green-500 bg-opacity-10 border-green-500" :
                  loadingStep === i + 1 ? "bg-purple-500 bg-opacity-10 border-purple-500" :
                  "bg-white bg-opacity-5 border-white border-opacity-10"}`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0 font-bold ${
                    loadingStep > i + 1 ? "bg-green-500 text-white" :
                    loadingStep === i + 1 ? "bg-purple-500 text-white" :
                    "bg-gray-700 text-gray-500"}`}>
                    {loadingStep > i + 1 ? "✓" : i + 1}
                  </div>
                  <span className={`text-sm ${
                    loadingStep > i + 1 ? "text-green-300" :
                    loadingStep === i + 1 ? "text-white" : "text-gray-600"}`}>{label}</span>
                  {loadingStep === i + 1 && (
                    <div className="ml-auto flex gap-1">
                      {[0,1,2].map(d => (
                        <div key={d} className="w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce"
                          style={{ animationDelay: `${d * 0.15}s` }} />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <button type="submit"
              className="w-full py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl font-semibold hover:opacity-90 transition-all">
              Activate AI Receptionist
            </button>
          )}
          <p className="text-center text-xs text-gray-500">{TRIAL_MINUTES} free minutes included. No credit card needed.</p>
        </form>
      )}

      {step === 2 && activeReceptionist && (
        <div className="space-y-4">
          <div className="text-center py-3">
            <h3 className="text-white text-xl font-bold">Your AI Receptionist is Live!</h3>
            <p className="text-gray-400 text-sm mt-1">
              Hi {firstName || "there"}, <strong className="text-white">{activeReceptionist.business_name}</strong> is ready.
            </p>
          </div>
          <LiveDashboard
            receptionist={activeReceptionist}
            voices={voices}
            onDelete={resetAll}
            onEdit={updated => setActiveReceptionist(updated)}
          />
          <button onClick={resetAll} className="w-full py-2 text-xs text-gray-600 hover:text-gray-400 transition-all">
            + Set up another receptionist
          </button>
        </div>
      )}
    </div>
  );
}
